import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { TimeRangeFinite } from "../../../../../types/types";

import {
  FORMAT_TIME,
  FORMAT_READABLE_DATE,
  ONE_DAY_OF_MS,
  getPnlFormattedHoursMinutes,
} from "../../../../../utils/timeUtils";

import styled from "styled-components/macro";
import {
  NAV_HEIGHT_PX,
  FONT_SIZE_MEDIUM,
  BREAKPOINT_MOBILE,
  FONT_SIZE_SMALL,
} from "../../../../../constants/constants";

import { darkBlue } from "../../../../../constants/colors";
import {
  useFilteredProcesses,
  useFormatZoned,
  useTrendsPageFilterState,
} from "utils/customHooks/genericHooks";
import { useFilteredShifts } from "utils/customHooks/trendChartHooks";
import { useDateMetadataDisplay } from "components/FlightTrends/DateRange/useDateMetadataDisplay";
import { animated, useTransition } from "@react-spring/web";
import { useEffect, useRef } from "react";

const PnlPopupTitleBarStyles = styled.div`
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
  height: ${NAV_HEIGHT_PX}px;
  flex-shrink: 0;
  background: ${darkBlue};
  display: flex;
  justify-content: center;
  .dateTimeWrapper {
    height: 100%;
    width: 100%;
    padding: 0 2.5em 0 1.5em;
    color: white;
    display: flex;
    align-items: center;
    font-size: ${FONT_SIZE_SMALL}px;
    justify-content: center;
    margin: auto;
    align-items: center;
  }
  .startAndEndWrapper {
    display: flex;
    flex-direction: column;
    .timezoneMultiplier {
      position: absolute;
      white-space: nowrap;
      font-size: 0.35em;
      top: 0.8em;
      left: 15%;
      right: 0;
      display: flex;
      width: fit-content;
      justify-self: center;
    }
  }
  .totalTime {
    margin-left: 1ch;
    position: relative;
    .totalTimeMultiplier {
      position: absolute;
      white-space: nowrap;
      font-size: 0.5em;
      bottom: -1.25em;
      left: 0;
      right: 0;
      width: 100%;
      display: flex;
      justify-content: center;
    }
  }
  @media (min-width: ${BREAKPOINT_MOBILE}px) {
    .dateTimeWrapper {
      padding: 0 1.5em;
      font-size: ${FONT_SIZE_MEDIUM}px;
    }
  }
  @media (min-width: 700px) {
    /* does datetime title fit horizontally */
    .startAndEndWrapper {
      flex-direction: row;
      align-items: center;
    }
    .dateTime.start {
      margin-right: 0.5ch;
    }
  }
  .btnCloseWrapper {
    display: flex;
    align-items: center;
    position: absolute;
    top: 8px;
    right: 8px;
    button {
      color: white;
      &:hover {
        background: rgba(255, 255, 255, 0.14901960784313725);
      }
    }
  }
`;

type TitleBarProps = {
  setPnlPopupTimeRange: any;
  pnlPopupTimeRange: TimeRangeFinite;
};
const PnlPopupTitleBar = ({
  setPnlPopupTimeRange,
  pnlPopupTimeRange,
}: TitleBarProps) => {
  return (
    <PnlPopupTitleBarStyles>
      <PnlPopupTitleContent {...{ pnlPopupTimeRange }} />
      <div className="btnCloseWrapper">
        <IconButton onClick={() => setPnlPopupTimeRange(null)} size="large">
          <CloseIcon color="inherit" />
        </IconButton>
      </div>
    </PnlPopupTitleBarStyles>
  );
};
export default PnlPopupTitleBar;

function PnlPopupTitleContent({
  pnlPopupTimeRange,
}: {
  pnlPopupTimeRange: TimeRangeFinite;
}) {
  const formatZoned = useFormatZoned();
  const { timezoneDisplay } = useDateMetadataDisplay();
  const filteredProcesses = useFilteredProcesses();
  const { isTrendsPageFilteredByShift } = useTrendsPageFilterState();
  const filteredShifts = useFilteredShifts();
  const startDateTime = formatZoned(
    Number(pnlPopupTimeRange.ge),
    `${FORMAT_READABLE_DATE} ${FORMAT_TIME}`
  );

  const endTime = formatZoned(Number(pnlPopupTimeRange.lt), FORMAT_TIME);
  const endDateTime = formatZoned(
    Number(pnlPopupTimeRange.lt),
    `${FORMAT_READABLE_DATE} ${FORMAT_TIME}`
  );
  const { hours, minutes } = getPnlFormattedHoursMinutes(
    pnlPopupTimeRange.lt - pnlPopupTimeRange.ge
  );
  const totalTime = Number(hours) > 99 ? `${hours}h` : `${hours}h${minutes}m`;

  const isChartMoreThanADay =
    pnlPopupTimeRange.lt - pnlPopupTimeRange.ge >= ONE_DAY_OF_MS;

  const numViewing = filteredProcesses.length;
  const { hours: totalHours, minutes: totalMinutes } =
    getPnlFormattedHoursMinutes(
      (pnlPopupTimeRange.lt - pnlPopupTimeRange.ge) * numViewing
    );
  const totalTotalTime =
    Number(totalHours) > 99
      ? `${totalHours}h`
      : `${totalHours}h${totalMinutes}m`;

  // normally shows e.g. "6 Processes ~ 1008h"
  // edge case for trends page + filtered by shift, show e.g. "6 Processes, 5 Shifts ~ 210h"
  const filteredShiftsInTimeRange = filteredShifts.filter(shift => {
    return (
      shift.range.ge >= pnlPopupTimeRange.ge &&
      shift.range.ge <= pnlPopupTimeRange.lt
    );
  });
  const totalTimeInShiftsMs = filteredShiftsInTimeRange.reduce(
    (acc, cur) => acc + (cur.range.lt - cur.range.ge),
    0
  );

  const { hours: totalHoursInShifts, minutes: totalMinutesInShifts } =
    getPnlFormattedHoursMinutes(totalTimeInShiftsMs * numViewing);
  const totalTimeInShifts =
    Number(totalHoursInShifts) > 99
      ? `${totalHoursInShifts}h`
      : `${totalHoursInShifts}h${totalMinutesInShifts}m`;

  const prevStartDateTimeRef = useRef(startDateTime);
  useEffect(() => {
    prevStartDateTimeRef.current = startDateTime;
  }, [startDateTime]);
  const isForwards = startDateTime > prevStartDateTimeRef.current;

  const transitionLeftRightTitle = useTransition(
    new Date(startDateTime).getTime(),
    {
      key: d => d,
      ...getTransitionLeftRightAnimationProps(isForwards, 70),
    }
  );
  return (
    <>
      {transitionLeftRightTitle((style, item) => {
        return (
          <animated.div style={style} className="dateTimeWrapper">
            <div className="startAndEndWrapper">
              <div className="dateTime start">
                <div className="timezoneMultiplier">{timezoneDisplay}</div>
                {startDateTime} -
              </div>
              <div className="dateTime end">
                {isChartMoreThanADay ? endDateTime : endTime}
              </div>
            </div>
            <div className="totalTime"></div>

            <div className="totalTime">
              {"("}
              {totalTime}
              {")"}
              <div className="totalTimeMultiplier">
                {numViewing} Processes{" "}
                {isTrendsPageFilteredByShift
                  ? `, ${filteredShiftsInTimeRange.length} shifts ~ ${totalTimeInShifts}`
                  : `~ ${totalTotalTime}`}
              </div>
            </div>
          </animated.div>
        );
      })}
    </>
  );
}
const getTransitionLeftRightAnimationProps = (isForwards, movementPx) => ({
  from: {
    transform: `translate(${isForwards ? movementPx : -movementPx}px,0px)`,
    opacity: 0,
  },
  enter: {
    transform: `translate(0px,0px)`,
    opacity: 1,
  },
  leave: {
    position: "absolute",
    transform: `translate(${
      isForwards ? -movementPx * 2 : movementPx * 2
    }px,0px)`,
    opacity: 0,
  },
});
